import { createFileRoute, Link } from "@tanstack/react-router";
import storyHero from "@/assets/story-hero.jpg";
import sourcing from "@/assets/leather-sourcing.jpg";
import craft from "@/assets/craft-hands.jpg";
import { BackButton } from "@/components/site/BackButton";

export const Route = createFileRoute("/our-story")({
  head: () => ({
    meta: [
      { title: "Our Story — KAKAIYS Maison of Leather" },
      {
        name: "description",
        content:
          "The story of KAKAIYS — a quiet leather house built on hand work, full-grain hides, and the slow conviction that one good thing outlasts a hundred fast ones.",
      },
      { property: "og:title", content: "Our Story — KAKAIYS" },
      {
        property: "og:description",
        content: "A quiet leather house, built one piece at a time.",
      },
      { property: "og:image", content: storyHero },
      { name: "twitter:image", content: storyHero },
      { property: "og:url", content: "https://kakaiys.com/our-story" },
    ],
      links: [{ rel: "canonical", href: "https://kakaiys.com/our-story" }],
  }),
  component: OurStoryPage,
});

function OurStoryPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative h-[90svh] min-h-[560px] w-full overflow-hidden bg-ink">
        <img
          src={storyHero}
          alt="A leather artisan's workbench at the KAKAIYS atelier"
          className="absolute inset-0 h-full w-full object-cover opacity-80 ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-ink/30 to-ink/90" />
        <div className="relative z-10 flex h-full items-end pb-20 md:pb-28">
          <div className="container-luxe text-bone">
            <BackButton fallback="/" className="mb-8" />
            <div className="eyebrow text-gold">Our Story</div>
            <h1 className="mt-5 font-serif text-5xl md:text-8xl leading-[0.95] text-gradient-gold">
              A house built
              <br />
              <span className="italic text-gradient-gold text-slate-50">slowly.</span>
            </h1>
            <p className="mt-6 max-w-md text-bone/90">
              KAKAIYS began with one cutting table, three pairs of hands, and a refusal to hurry.
            </p>
          </div>
        </div>
      </section>

      {/* EDITORIAL BRAND STORY (moved from home) */}
      <section className="bg-bone">
        <div className="container-luxe py-28 md:py-40">
          <div className="grid md:grid-cols-2 gap-14 md:gap-24 items-center">
            <div className="relative aspect-[4/5] overflow-hidden img-frame">
              <img
                src={storyHero}
                alt="Inside the KAKAIYS atelier — light through the workshop window"
                loading="lazy"
                decoding="async"
                width={1280}
                height={1600}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="md:pl-6 lg:pl-12">
              <div className="eyebrow mb-6">The Maison</div>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em] text-ink">
                A house of leather,
                <br />
                <span className="italic">quietly built.</span>
              </h2>
              <p className="mt-8 max-w-md text-base text-ash leading-[1.85] font-light">
                Founded on a single belief — that a leather piece, made with patience
                and full-grain hides, becomes more beautiful with the years. Every
                jacket, every bag begins on a cutting bench in our Sialkot atelier and
                ends in the hands of one artisan, who finishes it by their own name.
              </p>
              <Link
                to="/shop"
                preload="intent"
                className="mt-12 inline-block text-[11px] uppercase tracking-[0.32em] text-ink underline-grow"
              >
                Discover the Collection
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THE BEGINNING */}
      <section className="container-luxe py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
          <div>
            <div className="eyebrow mb-4 text-gold">Origin</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-gradient-gold">
              Sialkot, before the sun.
            </h2>
          </div>
          <div className="space-y-6 text-muted-foreground leading-relaxed">
            <p>
              KAKAIYS was founded in the old leather quarter of Sialkot, a city that has
              tanned hides for the world for over a hundred and fifty years. We took a small
              workshop at the end of a quiet lane, hung a single brass lamp over the cutting
              table, and made a promise to ourselves that has never changed: every piece would
              leave the door finished by hand, by one person, with their initials sewn into
              the lining.
            </p>
            <p>
              The name KAKAIYS belongs to the family. It is the maiden name of the woman who
              taught us, as children, that the things you make slowly are the things you keep.
            </p>
          </div>
        </div>
      </section>

      {/* SOURCING */}
      <section className="bg-ink text-bone">
        <div className="grid md:grid-cols-2 min-h-[80vh]">
          <div className="relative overflow-hidden img-frame">
            <img
              src={sourcing}
              alt="Full-grain leather hides drying at a heritage tannery"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="flex items-center px-8 py-20 md:px-20">
            <div className="max-w-md">
              <div className="eyebrow text-gold-soft mb-4">Sourcing</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-gradient-gold">
                Where our leather <span className="italic text-gradient-gold text-slate-50">comes from.</span>
              </h2>
              <div className="mt-6 space-y-5 text-bone/90 leading-relaxed text-sm md:text-base">
                <p>
                  Every hide that enters our atelier is full-grain — the strongest, longest-lived
                  layer of the skin, the one that develops a patina over decades rather than
                  cracking after seasons. We source from two heritage tanneries we have worked
                  with for years: a third-generation family tannery in Sialkot for our cowhide,
                  and a small vegetable-tannery in Tuscany for our finest lambskin.
                </p>
                <p>
                  Both are by-product hides from the regulated meat industry — never raised for
                  leather alone. Both tanneries are LWG-audited and use traditional vegetable and
                  chrome-free processes wherever possible. We visit them ourselves. We choose
                  every hide with our hands.
                </p>
                <p>
                  No factory, no broker, no anonymity. A leather jacket should be honest about
                  the animal that gave it, and the people who finished it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRAFT */}
      <section className="container-luxe py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center">
          <div className="relative aspect-[4/5] overflow-hidden img-frame order-2 md:order-1">
            <img
              src={craft}
              alt="Hand-stitching the lining of a KAKAIYS jacket"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="order-1 md:order-2">
            <div className="eyebrow mb-4 text-gold">The Craft</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-gradient-gold">
              Fourteen hours, <span className="italic text-gradient-gold text-slate-50">one artisan.</span>
            </h2>
            <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
              <p>
                A KAKAIYS jacket passes through one pair of hands from cutting board to final
                press. Not three stations, not seven sewers — one artisan, fourteen hours, one
                signature. Our master craftsmen have been with us since the beginning, and most
                were trained by their fathers before us.
              </p>
              <p>
                Every seam is sewn with waxed Italian thread on iron Pfaff machines that are
                older than any of us. Every lining is set by hand. Every button is sewn through
                a leather backer so it cannot tear free in a generation.
              </p>
              <p>
                When a piece finishes, the artisan signs the lining. That signature is our
                promise — and your proof that a person, not a process, made what you wear.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section
        className="text-bone py-24 md:py-32"
        style={{ backgroundColor: "oklch(0.18 0.045 40)" }}
      >
        <div className="container-luxe text-center max-w-3xl">
          <div className="eyebrow mb-6 text-gold">Our Promise</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight text-gradient-gold">
            Buy it once.
            <br />
            <span className="italic text-gradient-gold text-slate-50">Wear it forever.</span>
          </h2>
          <p className="mt-8 text-bone/90 leading-relaxed">
            Every KAKAIYS piece is covered by a lifetime repair service. Stitching wears,
            zippers tire, linings need replacing after twenty winters — send it back, and we
            will return it to you, restored. The leather is yours. The craft remains ours.
          </p>
          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            <Link
              to="/shop"
              className="btn-gold inline-flex items-center justify-center px-8 py-4 text-[11px] uppercase tracking-[0.28em]"
            >
              Discover the Collection
            </Link>
            <Link
              to="/journal"
              className="inline-flex items-center justify-center border border-gold/50 px-8 py-4 text-[11px] uppercase tracking-[0.28em] text-gold transition-all hover:bg-gold hover:text-ink"
            >
              Read the Journal
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
